/**
 * PasskeySettingsPage — Phase (Feb 2026)
 *
 * Verwaltung der Passkeys (Gerätelogin) für ALLE eingeloggten Nutzer.
 * Route: /passkeys
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Trash2, Plus, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import { listPasskeys, registerPasskey, deletePasskey, passkeySupported } from "../lib/passkey";
import { toast } from "sonner";

export default function PasskeySettingsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isWelcome = searchParams.get("welcome") === "1";
    const [creds, setCreds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nickname, setNickname] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user === false) navigate("/login?next=/passkeys");
    }, [user, navigate]);

    const load = async () => {
        try {
            setCreds(await listPasskeys());
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (user && user !== false) load(); }, [user]);

    const handleAdd = async () => {
        setBusy(true);
        const res = await registerPasskey(nickname.trim() || "Mein Gerät");
        setBusy(false);
        if (!res.ok) { toast.error(res.error); return; }
        toast.success("Passkey eingerichtet — Sie können sich künftig per Gerät anmelden.");
        setNickname("");
        load();
    };

    const handleDelete = async (id) => {
        try {
            await deletePasskey(id);
            toast.success("Passkey entfernt.");
            setCreds((c) => c.filter((x) => x.id !== id));
        } catch {
            toast.error("Passkey konnte nicht entfernt werden.");
        }
    };

    return (
        <div className="min-h-[calc(100vh-200px)] max-w-2xl mx-auto px-4 py-10" data-testid="passkey-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#111827] mb-6" data-testid="passkey-back">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </button>

            {isWelcome && (
                <div className="bg-[#FFCC00]/20 border-2 border-[#111827] rounded-sm p-4 mb-6 flex items-start justify-between gap-3" data-testid="passkey-welcome">
                    <div>
                        <div className="font-black text-base">Willkommen! Richten Sie Ihren Gerätelogin ein 🔑</div>
                        <p className="text-sm text-gray-700 mt-0.5">
                            Mit einem Passkey melden Sie sich künftig ohne Passwort an — schnell & sicher per Fingerabdruck, Gesicht oder Geräte-PIN. Optional, jederzeit nachholbar.
                        </p>
                    </div>
                    <Button variant="outline" className="rounded-sm shrink-0 border-[#111827]" onClick={() => navigate("/dashboard")} data-testid="passkey-skip-dashboard">
                        Später · zum Dashboard
                    </Button>
                </div>
            )}

            <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 bg-[#111827] rounded-sm flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-[#FFCC00]" />
                </div>
                <h1 className="font-display text-3xl font-black tracking-tight">Passkeys · Gerätelogin</h1>
            </div>
            <p className="text-gray-600 text-sm mb-8">
                Melden Sie sich künftig ohne Passwort an — mit Windows Hello, Touch ID, Fingerabdruck oder Geräte-PIN.
                Ihr E-Mail/Passwort-Login bleibt zusätzlich erhalten.
            </p>

            {!passkeySupported() && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4 rounded-sm mb-6" data-testid="passkey-unsupported">
                    Dieses Gerät oder dieser Browser unterstützt keine Passkeys.
                </div>
            )}

            {/* Neuen Passkey hinzufügen */}
            <div className="bg-white border border-gray-200 rounded-sm p-5 mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Neuen Passkey einrichten</div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        placeholder="Name (z. B. Büro-PC oder iPhone)"
                        className="rounded-sm"
                        data-testid="passkey-nickname"
                    />
                    <Button onClick={handleAdd} disabled={busy || !passkeySupported()} className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider shrink-0" data-testid="passkey-add-btn">
                        <Plus className="w-4 h-4 mr-1.5" /> {busy ? "Warte auf Gerät…" : "Passkey hinzufügen"}
                    </Button>
                </div>
            </div>

            {/* Liste */}
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Ihre Passkeys</div>
            {loading ? (
                <div className="text-gray-500 text-sm">Lade…</div>
            ) : creds.length === 0 ? (
                <div className="text-gray-500 text-sm bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="passkey-empty">
                    Noch keine Passkeys hinterlegt.
                </div>
            ) : (
                <ul className="space-y-2" data-testid="passkey-list">
                    {creds.map((c) => (
                        <li key={c.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-sm px-4 py-3" data-testid={`passkey-item-${c.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                                <div className="min-w-0">
                                    <div className="font-bold text-sm truncate">{c.nickname}</div>
                                    <div className="text-[11px] text-gray-500">
                                        Erstellt: {c.created_at ? new Date(c.created_at).toLocaleDateString("de-DE") : "—"}
                                        {c.last_used_at ? ` · Zuletzt: ${new Date(c.last_used_at).toLocaleDateString("de-DE")}` : ""}
                                    </div>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-sm shrink-0" data-testid={`passkey-delete-${c.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
